import React, { useState, useEffect, useRef } from "react"; // Added useRef
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetPatientListQuery, rootApi } from "../../api/rootApi";
import { setSelectedPatient, clearPatients } from "../../slices/patientSlice";
import { setSelectedUser, clearUser } from "../../slices/userSlice";
import "./patients.css";

import config from "../../config";
import {
  readLocalStorageJSON,
  writeLocalStorageJSON,
} from "../../utils/safeLocalStorage";

const images = { add: require("../../assets/images/add.png") };

const PatientDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);

  const selectedPatient = useSelector((state) => state.patient.selectedPatient);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Create a ref for the dropdown container
  const dropdownRef = useRef(null);

  // Function to handle clicks outside the dropdown
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  // Add event listener when the component mounts
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const resetToDefault = () => {
    setAuthToken(null);
    setPhoneNumber(null);
    setIsSignedIn(false);
    setShouldRender(false);
    localStorage.removeItem("selectedPatient");
    localStorage.removeItem("selectedUser");
    localStorage.removeItem("MRNO");
    localStorage.removeItem("name");
    localStorage.removeItem("email");
    dispatch(clearPatients());
    dispatch(clearUser());
    // Reset RTK Query cache so the next sign-in fetches a fresh patient list
    dispatch(rootApi.util.resetApiState());
  };

  // Monitor authToken & phoneNumber in localStorage.
  // Only reset API state when the user actually signs out — not on every
  // poll while already signed out (that wiped the slideshow cache every second).
  const wasSignedInRef = useRef(false);

  useEffect(() => {
    const checkLocalStorage = () => {
      const storedAuthToken = localStorage.getItem("authToken");
      const storedPhoneNumber = localStorage.getItem("phoneNumber");
      const hasSession = !!(storedAuthToken && storedPhoneNumber);

      if (hasSession) {
        wasSignedInRef.current = true;
        setAuthToken(storedAuthToken);
        setPhoneNumber(storedPhoneNumber);
        setIsSignedIn(true);
        setShouldRender(true);
      } else if (wasSignedInRef.current) {
        wasSignedInRef.current = false;
        resetToDefault();
      }
    };
    checkLocalStorage();
    const intervalId = setInterval(checkLocalStorage, 1000);
    return () => clearInterval(intervalId);
  }, [dispatch]);

  const {
    data: patients = [],
    isLoading,
    isError,
    refetch,
  } = useGetPatientListQuery(phoneNumber || "", { skip: !isSignedIn });

  // Refetch when authToken or phoneNumber changes
  useEffect(() => {
    if (authToken && phoneNumber) {
      refetch();
    }
  }, [authToken, phoneNumber, refetch]);

  // INITIALIZE selection: default on first render or from localStorage
  useEffect(() => {
    if (!isSignedIn || patients.length === 0) return;

    // If already have a stored patient, use that
    const storedPatient = readLocalStorageJSON("selectedPatient", null);
    if (storedPatient) {
      dispatch(setSelectedPatient(storedPatient));
      dispatch(setSelectedUser(storedPatient));
      return;
    }

    // Otherwise pick the API-defined default, or fall back to first in list
    const defaultPatient =
      patients.find((p) => p.isDefaultPatient) || patients[0];

    if (!defaultPatient) return;

    dispatch(setSelectedPatient(defaultPatient));
    dispatch(setSelectedUser(defaultPatient));

    writeLocalStorageJSON("selectedPatient", defaultPatient);
    localStorage.setItem("MRNO", defaultPatient.PatientMrno ?? "");
    localStorage.setItem("name", defaultPatient.PatientName ?? "");
    localStorage.setItem("email", defaultPatient.Email || "");
  }, [isSignedIn, patients, dispatch]);

  const handlePatientSelect = (patient) => {
    if (!patient) return;
    dispatch(setSelectedPatient(patient));
    dispatch(setSelectedUser(patient));
    writeLocalStorageJSON("selectedPatient", patient);
    localStorage.setItem("MRNO", patient.PatientMrno ?? "");
    localStorage.setItem("name", patient.PatientName ?? "");
    localStorage.setItem("email", patient.Email || "");
    setIsOpen(false);
  };

  const handleAddPatient = () => {
    navigate("/add-patient");
  };

  // Sync selection across tabs
  useEffect(() => {
    const handleStorageChange = () => {
      const storedPatient = readLocalStorageJSON("selectedPatient", null);
      if (storedPatient) {
        dispatch(setSelectedPatient(storedPatient));
        dispatch(setSelectedUser(storedPatient));
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch]);

  const handleRemovePatient = async (patientMrno) => {
    const confirmation = window.confirm(
      "Do you really want to remove this patient?"
    );
    if (!confirmation) return;

    const url = config.API_REMOVE_PATIENT_URL.replace(
      "{PhoneNo}",
      encodeURIComponent(phoneNumber)
    ).replace("{Mrno}", encodeURIComponent(patientMrno));

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        alert("Patient removed successfully.");

        // Check if the removed patient was the currently selected one
        const wasSelectedPatient = selectedPatient?.PatientMrno === patientMrno;

        // Refetch the patient list
        const { data: updatedPatients } = await refetch();

        if (
          wasSelectedPatient &&
          updatedPatients &&
          updatedPatients.length > 0
        ) {
          // Clear current selection from localStorage and Redux
          localStorage.removeItem("selectedPatient");
          localStorage.removeItem("selectedUser");
          localStorage.removeItem("MRNO");
          localStorage.removeItem("name");
          localStorage.removeItem("email");
          dispatch(clearPatients());
          dispatch(clearUser());

          // Select new default patient
          const newDefaultPatient =
            updatedPatients.find((p) => p.isDefaultPatient) ||
            updatedPatients[0];

          if (newDefaultPatient) {
            // Update Redux slices
            dispatch(setSelectedPatient(newDefaultPatient));
            dispatch(setSelectedUser(newDefaultPatient));

            writeLocalStorageJSON("selectedPatient", newDefaultPatient);
            localStorage.setItem("MRNO", newDefaultPatient.PatientMrno ?? "");
            localStorage.setItem("name", newDefaultPatient.PatientName ?? "");
            localStorage.setItem("email", newDefaultPatient.Email || "");
          }
        }
      } else {
        alert("Failed to remove the patient.");
      }
    } catch (error) {
      console.error("Error removing patient:", error);
      alert("An error occurred. Please try again.");
    }
  };

  if (!shouldRender) return null;

  return (
    <div className="pat-dropdown" ref={dropdownRef}>
      <div className="pat-dropdown-header" onClick={() => setIsOpen(!isOpen)}>
        {selectedPatient ? (
          <div>
            <p className="pat-name">{selectedPatient.PatientName}</p>
            <p className="pat-mr-number">
              MR No. {selectedPatient.PatientMrno}
            </p>
            <p className="pat-info">
              {selectedPatient.Gender}, {selectedPatient.Age}
            </p>
            <p className={selectedPatient.Allergy ? "pat-allergy-warning" : "pat-allergy-none"}>
              {selectedPatient.Allergy ? selectedPatient.Allergy : "No known allergies"}
            </p>
            {selectedPatient.isRemoveable && (
              <button
                onClick={() => handleRemovePatient(selectedPatient.PatientMrno)}
                className="remove-pat-button"
              >
                Remove Patient
              </button>
            )}
          </div>
        ) : (
          <p>Select Patient</p>
        )}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`dropdown-icon ${isOpen ? "expanded" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
      <div className={`pat-dropdown-list ${isOpen ? "show" : ""}`}>
        {isLoading ? (
          <p>Loading...</p>
        ) : isError ? (
          <p>Error fetching patients</p>
        ) : patients.length > 0 ? (
          <>
            {patients
              .filter(
                (patient) =>
                  patient.PatientMrno !== selectedPatient?.PatientMrno
              )
              .map((patient) => (
                <div key={patient.PatientMrno} className="pat-dropdown-item">
                  <div onClick={() => handlePatientSelect(patient)}>
                    <p className="pat-name">{patient.PatientName}</p>
                    <p className="pat-mr-number">
                      MR No. {patient.PatientMrno}
                    </p>
                    <p className="pat-info">
                      {patient.Gender}, {patient.Age}
                    </p>
                  </div>
                  {patient.isRemoveable && (
                    <button
                      onClick={() => handleRemovePatient(patient.PatientMrno)}
                      className="remove-pat-button"
                    >
                      Remove Patient
                    </button>
                  )}
                </div>
              ))}
            <button
              onClick={handleAddPatient}
              className="add-pat-button-dropdown"
            >
              <img src={images.add} alt="Add Patient" className="add-icon" />
              Add Patient
            </button>
          </>
        ) : (
          <p>No patients found</p>
        )}
      </div>
    </div>
  );
};

export default PatientDropdown;
